import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import getAssets from '@api/v4/assets/assets';
import getAttachments from '@api/v4/assets/attachments';
import getReservations from '@api/v4/attachments/reservations_NEW';
import { track as trackLogRocket } from '@helpers/logRocket';
import AttachmentSidebar from '@components/asset/modal/tabs/shared/attachment_sidebar/main';
import AssetDetails from '@components/asset/modal/tabs/workflow/asset_details/main';
import AttachmentDetails from '@components/asset/modal/tabs/workflow/attachment_details/main';
import './styles/main.scss';

const WorkflowTab = (props) => {
  const { assetKey, approver, displayAssetStatus } = props;

  const [asset, setAsset] = useState(null);
  const [attachments, setAttachments] = useState(null);
  const [activeCard, setActiveCard] = useState(null);
  const [fetchControllers, setFetchControllers] = useState([]);
  const [initialFetchControllers, setInitialFetchControllers] = useState([]); // initial page load fetches
  const [attachmentReservations, setAttachmentReservations] = useState(null);

  // TO DO use comments feature here to hide pieces if feature is disabled
  // implement this when we remove the status tab all together
  const commentsFeatureEnabled = true; // BFG.context.hasFeature('comments_and_annotations');

  const updateFetchControllers = (fetchController) => {
    setFetchControllers((prevFetchControllers) => ([...prevFetchControllers, fetchController]));
  };

  // separate method because clicking different attachments shouldn't cancel these fetches
  const updateInitialFetchControllers = (fetchController) => {
    setInitialFetchControllers((prevFetchControllers) => ([...prevFetchControllers, fetchController]));
  };

  const abortFetchControllers = (initial = null) => {
    (initial ? initialFetchControllers : fetchControllers).forEach((fetchController) => {
      if (!fetchController.signal.aborted) {
        fetchController.abort();
      }
    });
  };

  const retrieveAsset = () => {
    const options = {
      assetKey,
      params: {
        fields: 'availability,availability_end,availability_start,active_cdn_tracers,thumbnail_url',
      }
    };

    getAssets(options, updateInitialFetchControllers)
      .then((response) => {
        if (response.data) {
          setAsset(response.data);
          setActiveCard({ id: response.data.id, type: 'asset' });
        }
      })
      .catch((err) => console.log(err));
  };

  const retrieveAttachments = () => {
    const options = {
      assetKey,
      params: { fields: 'download_url,thumbnail_url,extension,thumbnailed' }
    };

    getAttachments(options, updateInitialFetchControllers)
      .then((response) => {
        if (response.data) {
          const sortedAttachments = response.data.sort((a, b) => (
            a.attributes.position - b.attributes.position
          ));
          setAttachments(sortedAttachments);
        }
      })
      .catch((err) => console.log(err));
  };

  const retrieveReservations = () => {
    const reservationFetches = [];
    const attachmentKeys = [];

    attachments.forEach((attachment) => {
      const options = {
        attachmentKey: attachment.id,
        params: { include: 'user' }
      };
      reservationFetches.push(getReservations(options, updateInitialFetchControllers));
      attachmentKeys.push(attachment.id);
    });

    Promise.all(reservationFetches)
      .then((responses) => {
        if (responses) {
          const reservations = {};
          responses.forEach((response, i) => { reservations[attachmentKeys[i]] = response; });
          setAttachmentReservations(reservations);
        }
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleUpdateActiveCard = (updatedActiveCard) => {
    abortFetchControllers();
    setActiveCard(updatedActiveCard);
  };

  const initializeTab = () => {
    retrieveAsset();
    retrieveAttachments();
  };

  useEffect(() => {
    trackLogRocket('comments_and_annotations');
    initializeTab();
    return () => {
      abortFetchControllers(true); // only abort initial page load fetches when unmounting
      abortFetchControllers();
    };
  }, []);

  useEffect(() => {
    if (attachments) {
      retrieveReservations();
    }
  }, [attachments]);

  return (
    <div className={`workflow-tab-container ${commentsFeatureEnabled ? '' : 'comments-feature-disabled'}`}>
      <AttachmentSidebar
        activeCard={activeCard}
        asset={asset}
        attachmentReservations={attachmentReservations || {}}
        attachments={attachments}
        displayAssetCard
        updateActiveCard={handleUpdateActiveCard}
      />
      {activeCard && asset && activeCard.type === 'asset' && (
        <AssetDetails
          approver={approver}
          asset={asset}
          displayAssetStatus={displayAssetStatus}
          retrieveAsset={retrieveAsset}
          setAsset={setAsset}
          updateFetchControllers={updateFetchControllers}
        />
      )}
      {activeCard && attachments && activeCard.type === 'attachment' && attachmentReservations && (
        <AttachmentDetails
          activeAttachment={attachments.find((attachment) => (attachment.id === activeCard.id))}
          activeCard={activeCard}
          asset={asset}
          attachmentReservations={attachmentReservations}
          commentsFeatureEnabled={commentsFeatureEnabled}
          initializeTab={initializeTab}
          setAttachmentReservations={setAttachmentReservations}
          setAttachments={setAttachments}
          updateFetchControllers={updateFetchControllers}
        />
      )}
    </div>
  );
};

WorkflowTab.propTypes = {
  assetKey: PropTypes.string.isRequired,
  approver: PropTypes.bool.isRequired,
  displayAssetStatus: PropTypes.bool.isRequired,
};

export default WorkflowTab;
